import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Users,
  UserCheck,
  Layers,
  ShieldCheck,
  Scale,
  ArrowLeft,
  FileText,
  Info,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '../components/ui/Card';
import { EsqueletoTarjeta } from '../components/ui/Esqueleto';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { BotonTema } from '../components/ui/BotonTema';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { ApiError } from '../lib/api';
import {
  propuestaApi,
  propuestaKeys,
  trazabilidadOk,
  type Dashboard,
} from '../lib/propuesta';

/**
 * Tablero del proceso (/tablero/:procesoId) — la foto agregada del hito M0:
 * asistencia, aportes por grupo, índice de trazabilidad y Gini de participación.
 * Regla C7: NUNCA se muestra nada por persona; solo agregados por grupo y el
 * Gini como número único (0 = participación equitativa).
 */
export function DashboardPage() {
  useSurfaceTheme('cabina', 'light');
  const { procesoId } = useParams<{ procesoId: string }>();
  const navigate = useNavigate();

  const dash = useQuery<Dashboard, ApiError>({
    queryKey: procesoId ? propuestaKeys.dashboard(procesoId) : ['dashboard', 'nada'],
    queryFn: () => propuestaApi.dashboard(procesoId!),
    enabled: Boolean(procesoId),
    refetchInterval: 10000,
  });

  const d = dash.data;

  return (
    <div className="min-h-screen bg-surface-base">
      <header className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(procesoId ? `/cabina?proceso=${procesoId}` : '/cabina')}
            leadingIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
          >
            Cabina
          </Button>
          <span className="hidden items-center gap-2 font-display text-sm font-semibold text-content sm:flex">
            <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
            Tablero del proceso
          </span>
        </div>
        <div className="flex items-center gap-2">
          {procesoId && (
            <Link to={`/propuesta/${procesoId}`}>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<FileText className="h-4 w-4" aria-hidden="true" />}
              >
                Propuesta
              </Button>
            </Link>
          )}
          <BotonTema />
        </div>
      </header>

      <main className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-6 sm:px-6">
        <div>
          <h1 className="font-display text-2xl font-semibold text-content sm:text-3xl">
            Tablero
          </h1>
          <p className="mt-1 flex items-center gap-1.5 font-sans text-sm text-content-muted">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Solo datos agregados. Nunca se muestran métricas por persona.
          </p>
        </div>

        {dash.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <EsqueletoTarjeta />
            <EsqueletoTarjeta />
            <EsqueletoTarjeta />
          </div>
        ) : dash.isError ? (
          <Card title="No pudimos cargar el tablero">
            <p className="font-sans text-sm text-content-muted">
              {(dash.error as ApiError)?.detail ??
                'Intentá de nuevo en un momento.'}
            </p>
          </Card>
        ) : d ? (
          <>
            {/* Fila de indicadores */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Asistencia data={d} />
              <Trazabilidad data={d} />
            </div>

            {/* Aportes por grupo */}
            <AportesPorGrupo data={d} />

            {/* Gini de participación */}
            <Gini valor={d.gini_participacion} />
          </>
        ) : null}
      </main>
    </div>
  );
}

/* ------------------------------- Piezas -------------------------------- */

function Tarjeta({
  icon: Icon,
  titulo,
  children,
}: {
  icon: LucideIcon;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface-raised p-5 shadow-sm">
      <h2 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content-muted">
        <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
        {titulo}
      </h2>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Asistencia({ data }: { data: Dashboard }) {
  const { participantes_total, con_aportes } = data.asistencia;
  const pct =
    participantes_total > 0
      ? Math.round((con_aportes / participantes_total) * 100)
      : 0;
  return (
    <Tarjeta icon={Users} titulo="Asistencia">
      <div className="flex items-end gap-2">
        <span className="font-display text-3xl font-bold tabular-nums text-content">
          {con_aportes}
        </span>
        <span className="mb-1 font-sans text-sm text-content-muted">
          de {participantes_total} participaron
        </span>
      </div>
      <p className="mt-1 flex items-center gap-1.5 font-sans text-xs text-content-muted">
        <UserCheck className="h-3.5 w-3.5 text-success" aria-hidden="true" />
        {pct}% de asistentes con al menos un aporte
      </p>
    </Tarjeta>
  );
}

function Trazabilidad({ data }: { data: Dashboard }) {
  const { indice, bloques_con_ref, bloques_total } = data.trazabilidad;
  const ok = trazabilidadOk(indice);
  return (
    <Tarjeta icon={ShieldCheck} titulo="Trazabilidad">
      <div className="flex items-center gap-3">
        <span className="font-display text-3xl font-bold tabular-nums text-content">
          {Math.round(indice)}%
        </span>
        <Badge variant={ok ? 'success' : 'warning'}>
          {ok ? 'Bien respaldada' : 'Baja evidencia'}
        </Badge>
      </div>
      <p className="mt-1 font-sans text-xs text-content-muted">
        {bloques_con_ref} de {bloques_total} bloques citan sus aportes de origen
      </p>
    </Tarjeta>
  );
}

function AportesPorGrupo({ data }: { data: Dashboard }) {
  const { total, por_grupo } = data.aportes;
  const grupos = [...por_grupo].sort((a, b) => b.n - a.n);
  const max = grupos.reduce((m, g) => Math.max(m, g.n), 0) || 1;

  return (
    <Card
      title="Aportes por grupo"
      footer={
        <span className="flex items-center gap-2">
          <Layers className="h-4 w-4" aria-hidden="true" />
          {total} aportes en total
        </span>
      }
    >
      {grupos.length === 0 ? (
        <p className="font-sans text-sm text-content-muted">
          Todavía no hay aportes registrados en el proceso.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {grupos.map((g) => {
            const pct = Math.round((g.n / max) * 100);
            const share = total > 0 ? Math.round((g.n / total) * 100) : 0;
            return (
              <li key={g.grupo} className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-sans text-sm font-medium text-content">
                    {g.grupo}
                  </span>
                  <span className="font-sans text-xs tabular-nums text-content-muted">
                    {g.n} · {share}%
                  </span>
                </div>
                <div
                  className="h-2.5 w-full overflow-hidden rounded-full bg-surface-sunken"
                  role="img"
                  aria-label={`${g.grupo}: ${g.n} aportes, ${share}% del total`}
                >
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Gini({ valor }: { valor: number }) {
  const v = Math.max(0, Math.min(1, valor));
  const pct = Math.round(v * 100);
  // Lectura sobria: bajo = equitativo (verde), alto = concentrado (aviso).
  const concentrado = v >= 0.4;
  return (
    <Card title="Gini de participación">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-baseline gap-2">
          <Scale className="h-6 w-6 text-primary" aria-hidden="true" />
          <span className="font-display text-3xl font-bold tabular-nums text-content">
            {v.toFixed(2)}
          </span>
        </div>
        <Badge variant={concentrado ? 'warning' : 'success'}>
          {concentrado ? 'Participación concentrada' : 'Participación pareja'}
        </Badge>
      </div>

      {/* Escala 0–1 con marcador */}
      <div className="mt-4">
        <div className="relative h-2.5 w-full rounded-full bg-surface-sunken">
          <div
            className="absolute top-1/2 h-4 w-1 -translate-y-1/2 rounded-full bg-content"
            style={{ left: `calc(${pct}% - 2px)` }}
            aria-hidden="true"
          />
        </div>
        <div className="mt-1.5 flex justify-between font-sans text-xs text-content-muted">
          <span>0 = equitativo (todos aportan parejo)</span>
          <span>1 = concentrado</span>
        </div>
      </div>

      <p className="mt-3 font-sans text-xs text-content-muted">
        Mide qué tan repartida está la participación entre quienes asistieron.
        Es un único número agregado: no revela cuánto aportó cada persona (C7).
      </p>
    </Card>
  );
}
